import { cloneDeep } from 'lodash';
import { getFramework, IFramework } from '@/framework';
import { loadImageAsync } from '@/utils/file-kit';
import { uploadBase64ToObs } from '@/utils/upload-kit';
import { IImageLayerProps, ILayerProps } from '@/version-types';
import { type ContextMenuPlugin } from '../context-menu/context-menu';
import { EDeformationImgType, getDeformationImgState } from './states/deformation-img';
import { EDeformationEvent } from './types';

export class DeformationImgPlugin {
    private framework: IFramework;
    private originLayer: ILayerProps | null = null;

    constructor(id: string) {
        this.framework = getFramework(id);
    }

    private tryGetContextMenu() {
        const contextMenu = this.framework.getInstance<ContextMenuPlugin>('ContextMenuPlugin');
        if (!contextMenu) {
            console.error('ContextMenuPlugin is not found');
            return null;
        }
        return contextMenu;
    }

    /** 获取绘制配置 */
    getConfig() {}

    /** 打开绘制 */
    open(type: EDeformationImgType, layerImage: HTMLCanvasElement) {
        const activeLayer = this.framework.operate.getActivePickLayer() || this.framework.operate.getActiveLayer();
        if (!activeLayer) return;
        this.originLayer = activeLayer;
        getDeformationImgState(this.framework.identity).setCurrentType(type);
        getDeformationImgState(this.framework.identity).setLayerImage(layerImage);
        getDeformationImgState(this.framework.identity).setIsDeformationImage(true);
        this.framework.stage.addOrUpdate({
            ...activeLayer,
            __isDeformationImage: true,
            __renderGLCanvas: false,
        });
        this.tryGetContextMenu()?.disableMenu();
        this.framework.operate.disablePick(false);
        this.framework.operate.setActiveLayer(activeLayer.id);
    }

    onConfirm(callback: (resultImage: HTMLCanvasElement) => void) {
        const handler = this.framework.events.on(EDeformationEvent.CONFIRM, callback);

        return () => {
            this.framework.events.off(handler);
        };
    }

    onCancel(callback: () => void) {
        const handler = this.framework.events.on(EDeformationEvent.CANCEL, callback);

        return () => {
            this.framework.events.off(handler);
        };
    }

    async save(image: HTMLCanvasElement, formatLayer: (layer: ILayerProps, originLayer: ILayerProps) => Promise<void>) {
        const activeLayer = this.framework.operate.getActivePickLayer() || this.framework.operate.getActiveLayer();
        if (!activeLayer) return;

        getDeformationImgState(this.framework.identity).setIsSaving(true);
        const result = await uploadBase64ToObs(image.toDataURL('image/png'));
        if (!result) {
            getDeformationImgState(this.framework.identity).setIsSaving(false);
            return;
        }
        getDeformationImgState(this.framework.identity).setIsDeformationImage(false);
        const newLayer = cloneDeep(activeLayer);
        const data = newLayer.data as IImageLayerProps;
        data.url = result.url;
        newLayer.width = image.width;
        newLayer.height = image.height;
        newLayer.__isDeformationImage = false;
        data.naturalWidth = image.width;
        data.naturalHeight = image.height;
        data.imageTransform = {
            scale: 1,
            rotate: 0,
            offsetX: 0,
            offsetY: 0,
            flipH: false,
            flipV: false,
            widthRatio: 1,
            heightRatio: 1,
        };
        await loadImageAsync(result.url);
        this.framework.stage.addOrUpdate({ ...newLayer });
        await formatLayer(newLayer, this.originLayer!);
        this.framework.operate.setActiveLayer(newLayer.id);
        getDeformationImgState(this.framework.identity).setIsSaving(false);
    }

    reset() {
        if (!this.originLayer) return;
        this.framework.stage.addOrUpdate({
            ...this.originLayer,
            __isDeformationImage: false,
        });
    }

    /** 关闭绘制 */
    close() {
        const activeLayer = this.framework.operate.getActivePickLayer() || this.framework.operate.getActiveLayer();
        if (!this.originLayer || !activeLayer) return;

        this.tryGetContextMenu()?.enableMenu();
        this.framework.operate.enablePick();
        this.framework.operate.setActiveLayer(this.originLayer.id);
        getDeformationImgState(this.framework.identity).setIsDeformationImage(false);
        this.destroy();
    }

    destroy() {
        getDeformationImgState(this.framework.identity).setIsDeformationImage(false);
        this.originLayer = null;
        getDeformationImgState(this.framework.identity).setLayerImage(null);
    }
}
